/**
 * UndoContext - Provides undo/redo stack management
 *
 * Uses operation-based undo with inverse operations, not full state snapshots.
 * Supports transaction grouping for batch operations.
 */

import {
	createContext,
	useContext,
	useCallback,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import {
	MAX_UNDO_LEVELS,
	generateTransactionId,
	type UndoableOperation,
	type UndoContextValue,
	type UndoStackState,
} from './Types'
import { executeInverse } from './ExecuteInverse'
import { executeRedo } from './ExecuteRedo'

// ========== Context ==========

const UndoContext = createContext<UndoContextValue | null>(null)

// ========== Provider Props ==========

interface UndoProviderProps {
	children: ReactNode
}

// ========== Provider ==========

export function UndoProvider({ children }: UndoProviderProps) {
	// Use ref for stack to avoid re-renders on every push
	// State is used only for derived values (canUndo, canRedo, labels)
	const stackRef = useRef<UndoStackState>({
		past: [],
		future: [],
	})

	// State for UI-facing values that trigger re-renders
	const [canUndo, setCanUndo] = useState(false)
	const [canRedo, setCanRedo] = useState(false)
	const [undoLabel, setUndoLabel] = useState<string | null>(null)
	const [redoLabel, setRedoLabel] = useState<string | null>(null)
	const [currentTransactionId, setCurrentTransactionId] = useState<string | null>(null)

	// Update UI state from stack
	const updateUIState = useCallback(() => {
		const { past, future } = stackRef.current
		setCanUndo(past.length > 0)
		setCanRedo(future.length > 0)
		const lastPast = past[past.length - 1]
		const lastFuture = future[future.length - 1]
		setUndoLabel(lastPast?.label ?? null)
		setRedoLabel(lastFuture?.label ?? null)
	}, [])

	// Get operations in a transaction (most recent group with same transactionId)
	const getTransactionOperations = useCallback((stack: UndoableOperation[]): UndoableOperation[] => {
		if (stack.length === 0) return []

		const lastOp = stack[stack.length - 1]
		if (!lastOp) return []

		if (!lastOp.transactionId) {
			return [lastOp]
		}

		// Find all consecutive operations with the same transactionId from the end
		const txId = lastOp.transactionId
		const ops: UndoableOperation[] = []
		for (let i = stack.length - 1; i >= 0; i--) {
			const op = stack[i]
			if (op && op.transactionId === txId) {
				ops.unshift(op)
			} else {
				break
			}
		}
		return ops
	}, [])

	// Push operation to past stack
	const pushOperation = useCallback((operation: UndoableOperation) => {
		const stack = stackRef.current

		// Clear future on new action
		stack.future = []

		// Add to past
		stack.past.push(operation)

		// Enforce MAX_UNDO_LEVELS
		if (stack.past.length > MAX_UNDO_LEVELS) {
			// Remove oldest operations to fit limit
			const excess = stack.past.length - MAX_UNDO_LEVELS
			stack.past = stack.past.slice(excess)
		}

		updateUIState()
	}, [updateUIState])

	// Undo most recent operation(s)
	const undo = useCallback(async () => {
		const stack = stackRef.current
		if (stack.past.length === 0) return

		// Get operations to undo (may be a transaction group)
		const opsToUndo = getTransactionOperations(stack.past)

		// Execute inverse operations in reverse order
		for (let i = opsToUndo.length - 1; i >= 0; i--) {
			const op = opsToUndo[i]
			if (!op) continue
			try {
				await executeInverse(op.inverseData)
			} catch (error) {
				console.error('Undo failed:', error)
				throw error
			}
		}

		// Move operations from past to future
		stack.past = stack.past.slice(0, stack.past.length - opsToUndo.length)
		stack.future.push(...opsToUndo)

		updateUIState()
	}, [getTransactionOperations, updateUIState])

	// Redo most recently undone operation(s)
	const redo = useCallback(async () => {
		const stack = stackRef.current
		if (stack.future.length === 0) return

		// Get operations to redo (may be a transaction group)
		const opsToRedo = getTransactionOperations(stack.future)

		// Execute redo operations in forward order
		for (const op of opsToRedo) {
			try {
				await executeRedo(op.redoData)
			} catch (error) {
				console.error('Redo failed:', error)
				throw error
			}
		}

		// Move operations from future to past
		stack.future = stack.future.slice(0, stack.future.length - opsToRedo.length)
		stack.past.push(...opsToRedo)

		updateUIState()
	}, [getTransactionOperations, updateUIState])

	// Clear future stack
	const clearFuture = useCallback(() => {
		stackRef.current.future = []
		updateUIState()
	}, [updateUIState])

	// Clear all history
	const clearAll = useCallback(() => {
		stackRef.current.past = []
		stackRef.current.future = []
		updateUIState()
	}, [updateUIState])

	// Start a transaction
	const startTransaction = useCallback((_label: string): string => {
		const txId = generateTransactionId()
		setCurrentTransactionId(txId)
		return txId
	}, [])

	// End a transaction
	const endTransaction = useCallback((transactionId: string) => {
		if (currentTransactionId === transactionId) {
			setCurrentTransactionId(null)
		}
	}, [currentTransactionId])

	const value: UndoContextValue = {
		canUndo,
		canRedo,
		undoLabel,
		redoLabel,
		currentTransactionId,
		pushOperation,
		undo,
		redo,
		clearFuture,
		clearAll,
		startTransaction,
		endTransaction,
	}

	return (
		<UndoContext.Provider value={value}>
			{children}
		</UndoContext.Provider>
	)
}

// ========== Hook ==========

export function useUndo(): UndoContextValue {
	const context = useContext(UndoContext)
	if (!context) {
		throw new Error('useUndo must be used within an UndoProvider')
	}
	return context
}
